'use client';

import NewsletterForm from './NewsletterForm';
import { useState, useEffect } from 'react';

export default function SubscribeBlock() {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [justSubscribed, setJustSubscribed] = useState(false);

  // Only check localStorage after mount to avoid hydration mismatch
  useEffect(() => {
    setMounted(true);
    const subscribed = localStorage.getItem('isSubscribed') === 'true';
    // Only set subscribed if we're not in the process of subscribing
    if (!justSubscribed) {
      setIsSubscribed(subscribed);
    }

    // Listen for storage events from other tabs/windows
    const handleStorageChange = (e: StorageEvent) => {
      // Only respond to storage events from other windows
      if (e.storageArea === localStorage && !justSubscribed) {
        const subscribed = localStorage.getItem('isSubscribed') === 'true';
        setIsSubscribed(subscribed);
      }
    };

    // Listen for custom subscription events from the modal
    const handleModalSubscription = () => {
      if (!justSubscribed) {
        console.log('Modal subscription success - starting animation sequence');
        // Start the same animation sequence as handleSuccess
        setJustSubscribed(true);
        
        // Hide other subscription elements immediately (but not this section)
        const style = document.createElement('style');
        style.textContent = `
          .subscribe-button-wrapper,
          .footer-newsletter-form {
            display: none !important;
          }
          .footer-newsletter-subscribed {
            display: flex !important;
          }
        `;
        document.head.appendChild(style);
        
        // Wait for the same timing as handleSuccess (2.5 seconds)
        setTimeout(() => {
          console.log('Starting modal collapse animation after 2.5 seconds');
          setIsAnimating(true);
          
          setTimeout(() => {
            console.log('Modal animation complete, setting localStorage');
            // Set localStorage and complete the process
            localStorage.setItem('isSubscribed', 'true');
            // Set cookie
            document.cookie = 'isSubscribed=true; path=/; max-age=' + (365 * 24 * 60 * 60);
            
            // Inject CSS to hide this section too
            const hideAllStyle = document.createElement('style');
            hideAllStyle.textContent = `
              .subscribe-section-wrapper {
                display: none !important;
              }
              .subscribe-section-line {
                display: flex !important;
                justify-content: center !important;
              }
            `;
            document.head.appendChild(hideAllStyle);
            
            // Dispatch storage event to update other components
            window.dispatchEvent(new Event('storage'));
            
            setIsSubscribed(true);
            setJustSubscribed(false);
          }, 700);
        }, 2500); // Same timing as handleSuccess
      }
    };

    window.addEventListener('storage', handleStorageChange as any);
    window.addEventListener('modalSubscriptionSuccess', handleModalSubscription);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange as any);
      window.removeEventListener('modalSubscriptionSuccess', handleModalSubscription);
    };
  }, [justSubscribed]);

  const handleSuccess = () => {
    console.log('SubscribeBlock handleSuccess called');
    // Mark that we just subscribed to prevent immediate collapse
    setJustSubscribed(true);
    
    // The form will show "Thank you for subscribing!" message
    // We need to wait for that to be visible before starting our animation
    
    // Hide other subscription elements immediately (but not this section)
    const style = document.createElement('style');
    style.textContent = `
      .subscribe-button-wrapper,
      .footer-newsletter-form {
        display: none !important;
      }
      .footer-newsletter-subscribed {
        display: flex !important;
      }
    `;
    document.head.appendChild(style);
    
    // Wait for the fade-in animation to complete (0.5s) plus 2 seconds to show the message
    setTimeout(() => {
      console.log('Starting collapse animation after 2.5 seconds');
      // Start the collapse animation
      setIsAnimating(true);
      
      // After the animation completes
      setTimeout(() => {
        console.log('Animation complete, setting localStorage');
        // NOW set localStorage and complete the process
        localStorage.setItem('isSubscribed', 'true');
        // Set cookie
        document.cookie = 'isSubscribed=true; path=/; max-age=' + (365 * 24 * 60 * 60);
        
        // Inject CSS to hide this section too
        const hideAllStyle = document.createElement('style');
        hideAllStyle.textContent = `
          .subscribe-section-wrapper {
            display: none !important;
          }
          .subscribe-section-line {
            display: flex !important;
            justify-content: center !important;
          }
        `;
        document.head.appendChild(hideAllStyle);
        
        // Dispatch storage event to update other components
        window.dispatchEvent(new Event('storage'));
        
        setIsSubscribed(true);
        setJustSubscribed(false); // Reset the flag
      }, 700); // Match the duration-700 animation
    }, 2500); // Wait 0.5s for fade-in + 2 seconds with success message visible
  };

  // Check if we should show collapsed state
  const shouldShowCollapsed = mounted && isSubscribed && !isAnimating && !justSubscribed;
  const shouldShowAnimating = mounted && isAnimating;
  
  // If collapsed, show minimal version (just the line)
  if (shouldShowCollapsed) {
    return (
      <div className="w-full flex justify-center">
        <div className="bg-[#000000] relative w-full max-w-[1440px] h-[1px]" />
      </div>
    );
  }
  
  return (
    <>
      {/* Subscribe Section wrapper - this whole section gets hidden */}
      <div className="subscribe-section-wrapper w-full flex justify-center">
        <div className="w-full max-w-[1440px]">
          <div className={`${
            shouldShowAnimating ? 'max-h-[1px] transition-all duration-700 ease-in-out overflow-hidden' : 'max-h-[1000px]'
          }`}>
            {/* Intro Section - White Background */}
            <div className={`w-full ${
              shouldShowAnimating ? 'opacity-0 transition-opacity duration-500' : 'opacity-100'
            }`}>
              <div
                className="bg-[#ffffff] relative w-full max-h-[278px] md:min-h-[200px] lg:h-[180px] py-8 md:py-10 lg:py-0"
                data-name="Intro"
                id="node-1106_2888"
              >
              <div className="absolute border-0 border-[#000000] border-solid inset-0 pointer-events-none" />
              <div className="relative size-full">
                <div className="box-border flex flex-col lg:grid lg:gap-6 lg:grid-cols-[repeat(11,_minmax(0px,_1fr))] lg:grid-rows-[auto] gap-4 md:gap-6 lg:gap-8 leading-[0] not-italic px-6 sm:px-8 md:px-10 lg:px-12 relative h-full text-[#000000] text-center lg:text-left items-center justify-center lg:content-center">
                  <div
                    className="lg:[grid-area:1_/_1_/_auto_/_span_6] box-border content-stretch flex flex-col gap-2 md:gap-4 lg:gap-4 items-center lg:items-start justify-start p-0 relative lg:self-center shrink-0 w-full lg:w-[635px]"
                    id="node-1106_2889"
                  >
                    <div
                      className="flex flex-col font-ibm-plex-sans-condensed font-normal justify-center relative shrink-0 text-[10px] sm:text-[16px] lg:text-[18px] tracking-[1px] sm:tracking-[1.6px] lg:tracking-[1.8px] uppercase w-full lg:w-auto"
                      id="node-1106_2890"
                    >
                      <p className="block leading-[18px] sm:leading-[17px] lg:leading-[18px] whitespace-nowrap">
                        Stay in the Loop
                      </p>
                    </div>
                    <div
                      className="leading-[32px] sm:leading-[36px] lg:leading-[44px] lg:min-w-full relative shrink-0 text-[24px] sm:text-[32px] lg:text-[40px] text-center lg:text-left lg:capitalize"
                      id="node-1106_2891"
                      style={{ fontFamily: 'var(--font-gilda-display), serif' }}
                    >
                      <p className="block mb-0">Around the Globe, Meet</p>
                      <p className="block mb-0">Best AI Makers and Tools</p>
                    </div>
                  </div>
                  <div
                    className="lg:[grid-area:1_/_7_/_auto_/_span_5] font-ibm-plex-sans relative lg:self-end shrink-0 text-[14px] sm:text-[16px] lg:text-[18px] w-full text-center lg:text-left"
                    id="node-1106_2892"
                  >
                    <p className="block leading-[24px] sm:leading-[25px] lg:leading-[28px]">
                      Explore a vibrant collection of the latest happenings in the global AI landscape. Discover innovative tools and talented makers who are shaping the future of artificial intelligence.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            </div>

            {/* Subscribe Section - Black Background */}
            <div className="w-full">
              <div
                className="bg-[#000000] relative w-full overflow-hidden h-auto lg:h-[80px]"
                data-name="Subscribe"
                id="node-1257_5167"
              >
              <div className={`flex flex-row items-center relative h-full ${
                shouldShowAnimating ? 'opacity-0 transition-opacity duration-500' : 'opacity-100'
              }`}>
                <div className="box-border flex flex-col lg:flex-row gap-3 sm:gap-4 lg:gap-8 items-center lg:items-center justify-center lg:justify-between px-4 sm:px-6 md:px-10 lg:px-12 py-5 sm:py-6 lg:py-0 relative w-full h-full">
                  <div className="font-ibm-plex-sans relative shrink-0 text-[#ffffff] text-[14px] sm:text-[16px] lg:text-[18px] leading-[20px] sm:leading-[24px] lg:leading-[28px] text-center lg:text-left">
                    <p className="block">
                      <span className="block sm:inline">Get the latest in AI:</span>
                      <span className="block sm:inline sm:ml-1">subscribe to our newsletter.</span>
                    </p>
                  </div>
                  <div className="flex flex-row gap-3 items-center w-full lg:w-auto justify-center lg:justify-end">
                    <NewsletterForm
                      source="subscribe-section"
                      variant="dark"
                      size="custom"
                      showLabel={false}
                      placeholder="EMAIL ADDRESS"
                      buttonText="SUBSCRIBE"
                      className=""
                      onSuccess={handleSuccess}
                    />
                  </div>
                </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Black line that appears after animation */}
          {shouldShowAnimating && (
            <div className="w-full flex justify-center">
              <div className="bg-[#000000] relative w-full max-w-[1440px] h-[1px] opacity-0 animate-fadeIn-delayed" />
            </div>
          )}
        </div>
      </div>
      
      {/* Line for subscribed users (only shows via CSS when subscribe-section-wrapper is hidden) */}
      <div className="subscribe-section-line w-full flex justify-center">
        <div className="bg-[#000000] relative w-full max-w-[1440px] h-[1px]" />
      </div>
    </>
  );
}